import { 
  format, parse, isValid, startOfWeek, endOfWeek, addDays, startOfMonth, 
  endOfMonth, addMonths, isBefore, isAfter, isSameDay, isSameMonth, 
  isSameYear, getYear, getMonth, getDate, getHours, getMinutes, setYear, 
  setMonth, setDate, setHours, setMinutes, isEqual, getDaysInMonth, 
  startOfDay, endOfDay, addYears, startOfYear, endOfYear, getDay,
  getSeconds, setSeconds
} from 'date-fns';

export default class DateFnsUtils {
  locale: any;
  yearFormat = "yyyy";
  yearMonthFormat = "MMMM yyyy";
  dateTime12hFormat = "MMMM do hh:mm aaaa";
  dateTime24hFormat = "MMMM do HH:mm";
  time12hFormat = "hh:mm a";
  time24hFormat = "HH:mm";
  dateFormat = "MMMM do";

  constructor({ locale }: { locale?: any } = {}) {
    this.locale = locale;
  }

  date(value?: any) {
    if (typeof value === "undefined") return new Date();
    if (value === null) return null;
    return new Date(value);
  }

  toJsDate(value: any) {
    return value;
  }

  parse(value: string, formatString: string) {
    if (value === "") return null;
    return parse(value, formatString, new Date(), { locale: this.locale });
  }

  format(date: Date, formatString: string) {
    return format(date, formatString, { locale: this.locale });
  }

  formatNumber(numberToFormat: string) {
    return numberToFormat;
  }

  isValid(date: any) {
    return isValid(date);
  }

  isNull(date: any) {
    return date === null;
  }

  isEqual(value: any, comparing: any) {
    if (value === null && comparing === null) return true;
    return isEqual(value, comparing);
  }

  isAfter(date: Date, comparing: Date) { return isAfter(date, comparing); }
  isAfterDay(date: Date, comparing: Date) { return isAfter(date, comparing) && !isSameDay(date, comparing); }
  isAfterYear(date: Date, comparing: Date) { return isAfter(date, comparing) && !isSameYear(date, comparing); }
  isBefore(date: Date, comparing: Date) { return isBefore(date, comparing); }
  isBeforeDay(date: Date, comparing: Date) { return isBefore(date, comparing) && !isSameDay(date, comparing); }
  isBeforeYear(date: Date, comparing: Date) { return isBefore(date, comparing) && !isSameYear(date, comparing); }
  isSameDay(date: Date, comparing: Date) { return isSameDay(date, comparing); }
  isSameMonth(date: Date, comparing: Date) { return isSameMonth(date, comparing); }
  isSameYear(date: Date, comparing: Date) { return isSameYear(date, comparing); }
  isSameHour(date: Date, comparing: Date) { return isSameDay(date, comparing) && getHours(date) === getHours(comparing); }

  getYear(date: Date) { return getYear(date); }
  getMonth(date: Date) { return getMonth(date); }
  getDate(date: Date) { return getDate(date); }
  getHours(date: Date) { return getHours(date); }
  getMinutes(date: Date) { return getMinutes(date); }
  getSeconds(date: Date) { return getSeconds(date); }
  getDay(date: Date) { return getDay(date); }
  getDiff(date: Date, comparing: Date) { return date.getTime() - comparing.getTime(); }

  setYear(date: Date, count: number) { return setYear(date, count); }
  setMonth(date: Date, count: number) { return setMonth(date, count); }
  setDate(date: Date, count: number) { return setDate(date, count); }
  setHours(date: Date, count: number) { return setHours(date, count); }
  setMinutes(date: Date, count: number) { return setMinutes(date, count); }
  setSeconds(date: Date, count: number) { return setSeconds(date, count); }
  
  addDays(date: Date, count: number) { return addDays(date, count); }
  addMonths(date: Date, count: number) { return addMonths(date, count); }
  addYears(date: Date, count: number) { return addYears(date, count); }

  mergeDateAndTime(date: Date, time: Date) {
    return this.setMinutes(this.setHours(date, this.getHours(time)), this.getMinutes(time));
  }

  getDaysInMonth(date: Date) { return getDaysInMonth(date); }
  getNextMonth(date: Date) { return addMonths(date, 1); }
  getPreviousMonth(date: Date) { return addMonths(date, -1); }
  getNextYear(date: Date) { return addYears(date, 1); }
  getPreviousYear(date: Date) { return addYears(date, -1); }
  startOfDay(date: Date) { return startOfDay(date); }
  endOfDay(date: Date) { return endOfDay(date); }
  startOfWeek(date: Date) { return startOfWeek(date, { locale: this.locale }); }
  endOfWeek(date: Date) { return endOfWeek(date, { locale: this.locale }); }
  startOfMonth(date: Date) { return startOfMonth(date); }
  endOfMonth(date: Date) { return endOfMonth(date); }
  startOfYear(date: Date) { return startOfYear(date); }
  endOfYear(date: Date) { return endOfYear(date); }

  getMonthArray(date: Date) {
    const firstMonth = startOfYear(date);
    return [0,1,2,3,4,5,6,7,8,9,10,11].map(i => setMonth(firstMonth, i));
  }

  getYearRange(start: Date, end: Date) {
    const startDate = startOfYear(start);
    const endDate = endOfYear(end);
    const years = [];
    let current = startDate;
    while (isBefore(current, endDate) || isSameYear(current, endDate)) {
      years.push(current);
      current = addYears(current, 1);
    }
    return years;
  }

  getWeekdays() {
    const start = startOfWeek(new Date(), { locale: this.locale });
    return [0, 1, 2, 3, 4, 5, 6].map((diff) => 
      format(addDays(start, diff), 'EEEEEE', { locale: this.locale })
    );
  }

  getWeekArray(date: Date) {
    const start = startOfWeek(startOfMonth(date), { locale: this.locale });
    const end = endOfWeek(endOfMonth(date), { locale: this.locale });
    const nestedWeeks = [];
    let current = start;
    while (isBefore(current, end)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(current);
        current = addDays(current, 1);
      }
      nestedWeeks.push(week);
    }
    return nestedWeeks;
  }
  
  getMeridiemText(ampm: "am" | "pm") { return ampm === "am" ? "AM" : "PM"; }
  getCalendarHeaderText(date: Date) { return format(date, 'MMMM yyyy', { locale: this.locale }); }
  getYearText(date: Date) { return format(date, 'yyyy', { locale: this.locale }); }
  getDatePickerHeaderText(date: Date) { return format(date, 'EEE, MMM d', { locale: this.locale }); }
  getDateTimePickerHeaderText(date: Date) { return format(date, 'MMM d', { locale: this.locale }); }
  getMonthText(date: Date) { return format(date, 'MMMM', { locale: this.locale }); }
  getDayText(date: Date) { return format(date, 'd', { locale: this.locale }); }
  getHourText(date: Date, ampm: boolean) { return format(date, ampm ? 'hh' : 'HH', { locale: this.locale }); }
  getMinuteText(date: Date) { return format(date, 'mm', { locale: this.locale }); }
  getSecondText(date: Date) { return format(date, 'ss', { locale: this.locale }); }
}